/**
 * A GFM autolink renders the URL itself as its visible text. SharePoint,
 * presigned-S3 and Google Drive links routinely run past 200 characters, which
 * is unreadable and — because a URL offers few line-break opportunities —
 * pushes the containing card wider than its column.
 *
 * These helpers shorten only the *display* text; the `href` is always kept
 * whole so the link still resolves.
 */

/** Display budget, in characters, for an autolinked URL. */
export const MAX_AUTOLINK_DISPLAY_CHARS = 64;

/**
 * Middle-ellipsis a URL, preserving the two parts a reader actually uses to
 * identify a link: the origin (where it goes) and the tail (which document,
 * or the significant query parameter).
 */
export function truncateUrlMiddle(url: string, max = MAX_AUTOLINK_DISPLAY_CHARS): string {
  if (url.length <= max) return url;

  // Default to an even split, then widen the head to the full origin when that
  // still leaves a useful tail — a bare `https://contoso.share…` reads worse
  // than a whole hostname.
  let head = Math.max(1, Math.floor((max - 1) / 2));
  try {
    const { origin } = new URL(url);
    if (origin.length > head && origin.length <= max - 9) head = origin.length;
  } catch {
    // Not absolute (remark autolinks bare `www.…` too) — keep the even split.
  }

  return `${url.slice(0, head)}…${url.slice(url.length - (max - 1 - head))}`;
}

/**
 * True when `text` is the visible text remark generated for an autolink of
 * `href`, rather than a label an author wrote. remark-gfm prefixes bare hosts
 * and emails, so `www.a.com` → `http://www.a.com` and `a@b.com` →
 * `mailto:a@b.com` must both count as a match.
 */
export function isAutolinkText(text: string, href: string): boolean {
  return href === text || href === `http://${text}` || href === `mailto:${text}`;
}
